// DeleteEspacioModal.tsx — Co Marca — Eliminar Espacio
import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Trash2, X, AlertTriangle } from 'lucide-react';
import { deleteEspacio, Espacio } from '../../services/espacioService';

interface DeleteEspacioModalProps {
    isOpen: boolean; onClose: () => void; espacio: Espacio|null; onDeleted: () => void;
}

const colors = {
    primary:        '#c1348b',
    primaryDark:    '#8f1f64',
    secondary:      '#61257b',
    secondaryLight: '#8b3fa8',
    accentDark:     '#ea8406',
};

const css = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800;900&display=swap');
.dem-modal *{font-family:'DM Sans',sans-serif;box-sizing:border-box;}
@keyframes demFadeIn{from{opacity:0;}to{opacity:1;}}
@keyframes demSlideUp{from{opacity:0;transform:translateY(20px) scale(.97);}to{opacity:1;transform:translateY(0) scale(1);}}
@keyframes spin{to{transform:rotate(360deg);}}
.dem-overlay{animation:demFadeIn .2s ease both;}
.dem-dialog{animation:demSlideUp .3s cubic-bezier(.16,1,.3,1) both;}
.dem-btn-delete{display:inline-flex;align-items:center;gap:7px;background:linear-gradient(135deg,${colors.primary},${colors.primaryDark});color:#fff;border:none;border-radius:11px;padding:10px 22px;font-size:.88rem;font-weight:800;cursor:pointer;box-shadow:0 4px 14px rgba(193, 52, 139,.28);transition:all .22s;flex:1;justify-content:center;}
.dem-btn-delete:hover:not(:disabled){transform:translateY(-1px);box-shadow:0 8px 22px rgba(193, 52, 139,.36);}
.dem-btn-delete:disabled{background:#e2e8f0;color:#94a3b8;box-shadow:none;cursor:not-allowed;}
.dem-btn-cancel{display:inline-flex;align-items:center;gap:7px;background:#f8fafc;border:1.5px solid #e2e8f0;color:#374151;border-radius:11px;padding:10px 18px;font-size:.88rem;font-weight:700;cursor:pointer;transition:all .2s;flex:1;justify-content:center;}
.dem-btn-cancel:hover{background:#f1f5f9;}
`;

const DeleteEspacioModal: React.FC<DeleteEspacioModalProps> = ({ isOpen, onClose, espacio, onDeleted }) => {
    const [isDeleting, setIsDeleting] = useState(false);
    const [error, setError]           = useState<string|null>(null);

    const handleDelete = async () => {
        if (!espacio) return;
        setIsDeleting(true); setError(null);
        try { await deleteEspacio(espacio.id); onDeleted(); onClose(); }
        catch { setError('Ocurrió un error al eliminar. Intenta de nuevo.'); }
        finally { setIsDeleting(false); }
    };

    if (!isOpen || !espacio) return null;

    return createPortal(
        <>
            <style>{css}</style>
            <div className="dem-modal" style={{ position:'fixed', inset:0, zIndex:99999, display:'flex', alignItems:'center', justifyContent:'center', padding:'16px' }}>
                <div className="dem-overlay" onClick={onClose} style={{ position:'absolute', inset:0, background:'rgba(15,23,42,.6)', backdropFilter:'blur(6px)' }}/>
                <div className="dem-dialog" style={{ position:'relative', background:'#fff', borderRadius:'24px', width:'100%', maxWidth:'440px', boxShadow:'0 32px 80px rgba(0,0,0,.2)', border:'1.5px solid #f1f5f9', overflow:'hidden' }}>
                    <div style={{ height:'4px', background:`linear-gradient(90deg,${colors.primary},${colors.primaryDark},${colors.primary}80)` }}/>
                    <div style={{ padding:'24px 24px 0', display:'flex', alignItems:'flex-start', justifyContent:'space-between' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:'12px' }}>
                            <div style={{ width:'44px', height:'44px', borderRadius:'14px', background:`${colors.primary}12`, border:`1.5px solid ${colors.primary}22`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                                <AlertTriangle size={20} color={colors.primary}/>
                            </div>
                            <div>
                                <h3 style={{ fontSize:'1.05rem', fontWeight:'900', color:'#0f172a', letterSpacing:'-.02em' }}>Confirmar eliminación</h3>
                                <p style={{ fontSize:'.72rem', color:'#94a3b8', marginTop:'2px' }}>Esta acción no se puede deshacer</p>
                            </div>
                        </div>
                        <button onClick={onClose} disabled={isDeleting} style={{ width:'30px', height:'30px', borderRadius:'9px', border:'1.5px solid #f1f5f9', background:'#f8fafc', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}
                            onMouseEnter={e=>(e.currentTarget as HTMLButtonElement).style.background='#f1f5f9'}
                            onMouseLeave={e=>(e.currentTarget as HTMLButtonElement).style.background='#f8fafc'}>
                            <X size={14} color="#64748b"/>
                        </button>
                    </div>
                    <div style={{ padding:'20px 24px 24px' }}>
                        <div style={{ display:'flex', gap:'4px', marginBottom:'18px' }}>
                            {[colors.primary,colors.secondary,colors.accentDark,colors.secondaryLight].map((c,i)=>(
                                <div key={i} style={{ height:'2px', width:i===0?'16px':'5px', borderRadius:'2px', background:c, opacity:.4 }}/>
                            ))}
                        </div>
                        {error && (
                            <div style={{ display:'flex', alignItems:'center', gap:'8px', background:`${colors.primary}08`, border:`1.5px solid ${colors.primary}22`, borderRadius:'10px', padding:'10px 14px', marginBottom:'14px' }}>
                                <X size={14} color={colors.primary}/>
                                <span style={{ fontSize:'.82rem', color:colors.primaryDark, fontWeight:'600' }}>{error}</span>
                            </div>
                        )}
                        <p style={{ fontSize:'.9rem', color:'#475569', lineHeight:1.65, marginBottom:'14px' }}>
                            ¿Estás seguro que deseas eliminar el espacio{' '}
                            <strong style={{ color:colors.primaryDark, fontWeight:'900' }}>"{espacio.nombre}"</strong>?
                        </p>
                        <div style={{ background:`${colors.primary}07`, border:`1.5px solid ${colors.primary}18`, borderRadius:'12px', padding:'12px 14px', display:'flex', alignItems:'flex-start', gap:'10px', marginBottom:'22px' }}>
                            <Trash2 size={14} color={colors.primary} style={{ flexShrink:0, marginTop:'1px' }}/>
                            <p style={{ fontSize:'.78rem', color:colors.primaryDark, fontWeight:'600', lineHeight:1.5 }}>
                                Se eliminará permanentemente el espacio y toda la configuración asociada.
                            </p>
                        </div>
                        <div style={{ display:'flex', gap:'10px' }}>
                            <button onClick={onClose} disabled={isDeleting} className="dem-btn-cancel">Cancelar</button>
                            <button onClick={handleDelete} disabled={isDeleting} className="dem-btn-delete">
                                {isDeleting ? <><span style={{ width:'13px',height:'13px',border:'2px solid rgba(255,255,255,.4)',borderTopColor:'#fff',borderRadius:'50%',animation:'spin .7s linear infinite',display:'inline-block' }}/> Eliminando...</> : <><Trash2 size={14}/> Eliminar</>}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>,
        document.body
    );
};

export default DeleteEspacioModal;